import { useState } from 'react'

import {
  emptyDeviceFilters,
  type DeviceFilters,
  type DeviceStatusFilter,
} from '@/features/devices/types/device-filters'
import { cn } from '@/lib/utils'
import { Button, Offcanvas, InputField } from '@/shared/components'

type DeviceFiltersPanelProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  filters: DeviceFilters
  onApply: (filters: DeviceFilters) => void
}

const statusOptions: { value?: DeviceStatusFilter; label: string }[] = [
  { value: undefined, label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
]

export const DeviceFiltersPanel = ({
  open,
  onOpenChange,
  filters,
  onApply,
}: DeviceFiltersPanelProps) => {
  const [draft, setDraft] = useState<DeviceFilters>(filters)
  const [wasOpen, setWasOpen] = useState(open)

  if (open !== wasOpen) {
    setWasOpen(open)

    if (open) {
      setDraft(filters)
    }
  }

  const handleApply = () => {
    onApply(draft)
    onOpenChange(false)
  }

  const handleClear = () => {
    const cleared = emptyDeviceFilters()
    setDraft(cleared)
    onApply(cleared)
    onOpenChange(false)
  }

  return (
    <Offcanvas
      open={open}
      onOpenChange={onOpenChange}
      title="Filter devices"
      footer={
        <div className="flex gap-3">
          <Button
            type="button"
            variant="secondary"
            className="flex-1"
            onClick={handleClear}
          >
            Clear
          </Button>

          <Button
            type="button"
            variant="action"
            className="flex-1"
            onClick={handleApply}
          >
            Filter
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-6">
        <InputField
          id="device-filter-serial-number"
          label="Serial number"
          placeholder="Search by serial number"
          value={draft.serial_number}
          onChange={(serial_number) =>
            setDraft((current) => ({ ...current, serial_number }))
          }
        />

        <InputField
          id="device-filter-location"
          label="Current location"
          placeholder="Search by location"
          value={draft.location}
          onChange={(location) =>
            setDraft((current) => ({ ...current, location }))
          }
        />

        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-semibold text-foreground">Status</legend>

          <div className="flex flex-wrap gap-2">
            {statusOptions.map((option) => {
              const isSelected = draft.status === option.value

              return (
                <button
                  key={option.label}
                  type="button"
                  aria-pressed={isSelected}
                  className={cn(
                    'rounded-button border px-4 py-2 text-sm font-semibold transition-colors',
                    isSelected
                      ? 'border-primary-dark bg-primary-dark text-white'
                      : 'border-outline bg-white text-foreground hover:bg-surface-muted'
                  )}
                  onClick={() =>
                    setDraft((current) => ({ ...current, status: option.value }))
                  }
                >
                  {option.label}
                </button>
              )
            })}
          </div>
        </fieldset>
      </div>
    </Offcanvas>
  )
}
